import { onMounted } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { getWordList } from "@/common/graphql/expression";
import { getChainInfo, getChainPaymentMethod } from "@/common/graphql/chain";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  GetChainDocument,
  GetChainQuery,
  GetShopDocument,
  GetShopQuery,
  GetMemberDocument,
  GetMemberQuery,
  GetTmpMemberDocument,
  GetTmpMemberQuery,
} from "@/generated/graphql";
import deliveryRegisterInputStore from "@/store/deliveryRegisterInputStore";
import type { Ref } from "vue";
import type { Member } from "../types";
import { DELIVERY_REQUEST_REGISTER_INPUT_FILE_NAME } from "../constants";

/**
 * 初期表示時の各種マスタ取得・初期値反映に必要な依存をまとめた型。
 */
interface UseDeliveryRequestRegisterInitParams {
  wordList: Ref<Record<string, string>>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  backToStart: Ref<boolean>;
  getAvailableDate: Ref<string>;
  deliveryRequestCreateInput: any;
  operationInput: any;
  house_card_display: Ref<number>;
  house_card_validation: Ref<string>;
  house_card_required: Ref<number>;
  register_no_max_length: Ref<number>;
  receipt_no_max_length: Ref<number>;
  register_no_validation: Ref<string>;
  receipt_no_validation: Ref<string>;
  shopData: Ref<{ name: string; shippingLabelPrintFlg: number; deliveryOrderPrintFlg: number } | null>;
  paymentMethodItems: Ref<Array<string | undefined>>;
  unitItems: Ref<number[]>;
  is_hide_delivery_time_on_spot: Ref<number>;
  memberData: Ref<Member | null | undefined>;
  tmpMemberData: Ref<any>;
  house_card_before: Ref<string>;
  house_card_change: Ref<boolean>;
  originalAddress: Ref<string>;
  initializeAddress: () => Promise<void>;
  getAreaList: () => Promise<void>;
  commmaMoney: (value: string) => Promise<void>;
  onChangePaymentMethod: (item: string) => Promise<void>;
}

/**
 * 画面初期化処理（文言/店舗/会員/支払方法/住所情報の取得）を扱う composable。
 */
export const useDeliveryRequestRegisterInit = ({
  wordList,
  errdialog,
  errMessage,
  backToStart,
  getAvailableDate,
  deliveryRequestCreateInput,
  operationInput,
  house_card_display,
  house_card_validation,
  house_card_required,
  register_no_max_length,
  receipt_no_max_length,
  register_no_validation,
  receipt_no_validation,
  shopData,
  paymentMethodItems,
  unitItems,
  is_hide_delivery_time_on_spot,
  memberData,
  tmpMemberData,
  house_card_before,
  house_card_change,
  originalAddress,
  initializeAddress,
  getAreaList,
  commmaMoney,
  onChangePaymentMethod,
}: UseDeliveryRequestRegisterInitParams) => {
  const runInitialLoad = async () => {
    try {
      deliveryRequestCreateInput.chain_code = localStorageGetChainCode();
      deliveryRequestCreateInput.customer_code = localStorageGetCustomerCode();
      deliveryRequestCreateInput.shop_code = localStorageGetShopCode();

      //配達便データの取得日と現在の日付を比較する
      const _date = new Date();
      const _currentDate = _date.getDate().toString();

      if (getAvailableDate.value !== _currentDate) {
        errdialog.value = true;
        errMessage.value =
          "配達便の情報が前日の状態の可能性があります。\n配達依頼登録画面に戻り、再度受付登録を行ってください。";
        backToStart.value = true;
      }

      const apolloClient = await generateApolloClient();

      //文言データを取得する
      wordList.value = await getWordList(
        localStorageGetChainCode(),
        DELIVERY_REQUEST_REGISTER_INPUT_FILE_NAME,
        localStorageGetCustomerCode()
      );

      //チェーン情報を取得する
      const result = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      if (result !== undefined && result !== null) {
        house_card_display.value = result.house_card_display
          ? result.house_card_display
          : 0;
        house_card_validation.value = result.house_card_validation
          ? result.house_card_validation
          : "";
        if (
          house_card_validation.value !== "" &&
          house_card_validation.value.indexOf("required") !== -1
        ) {
          house_card_required.value = 1;
        }

        register_no_max_length.value = result.register_no_max_length
          ? result.register_no_max_length
          : 0;
        receipt_no_max_length.value = result.receipt_no_max_length
          ? result.receipt_no_max_length
          : 0;

        register_no_validation.value =
          "numeric|max:" +
          (result.register_no_max_length
            ? result.register_no_max_length.toString()
            : "0");
        receipt_no_validation.value =
          "numeric|max:" +
          (result.receipt_no_max_length
            ? result.receipt_no_max_length.toString()
            : "0");
      }

      /**
       * 店舗情報取得
       */
      try {
        const shop_data: ApolloQueryResult<GetShopQuery> =
          await apolloClient.query<GetShopQuery>({
            query: GetShopDocument,
            variables: {
              chain_code: localStorageGetChainCode(),
              customer_code: localStorageGetCustomerCode(),
              shop_code: localStorageGetShopCode(),
            },
            fetchPolicy: "no-cache",
          });
        shopData.value = {
          name: shop_data.data!.getShop!.name,
          shippingLabelPrintFlg: shop_data.data!.getShop!.shipping_label_print_flg,
          deliveryOrderPrintFlg: shop_data.data!.getShop!.delivery_order_print_flg,
        };
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "店舗情報の取得に失敗しました。";
          throw err;
        } else {
          throw new Error("店舗情報の取得に失敗しました。");
        }
      }

      //決済方法の取得処理
      const paymentList = await getChainPaymentMethod(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      paymentMethodItems.value = paymentList!.map(
        (paymentList) => paymentList?.payment_method
      );
      if (paymentMethodItems.value.length == 1) {
        deliveryRequestCreateInput.payment_method =
          paymentMethodItems.value[0]!;
        await onChangePaymentMethod(deliveryRequestCreateInput.payment_method);
      }

      // 都度便の場合に配達希望時間を非表示とするフラグを設定
      const chainResult = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode()
      );
      if (chainResult !== undefined && chainResult !== null) {
        is_hide_delivery_time_on_spot.value =
          chainResult.is_hide_delivery_time_on_spot
            ? chainResult.is_hide_delivery_time_on_spot
            : 0;
      }

      /**
       * 個口数
       */
      try {
        const max_unit: ApolloQueryResult<GetChainQuery> =
          await apolloClient.query<GetChainQuery>({
            query: GetChainDocument,
            variables: {
              chain_code: deliveryRequestCreateInput.chain_code,
              customer_code: deliveryRequestCreateInput.customer_code,
            },
            fetchPolicy: "no-cache",
          });

        for (let i = 0; i < Number(max_unit.data.getChain!.max_unit); i++) {
          unitItems.value.push(i + 1);
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "個口数の取得に失敗しました。";
          throw err;
        } else {
          throw new Error("個口数の取得に失敗しました。");
        }
      }

      //住所情報を取得する
      await initializeAddress();

      /**
       * 会員IDから情報取得する
       */
      if (deliveryRegisterInputStore.user_id !== "") {
        try {
          const member: ApolloQueryResult<GetMemberQuery> =
          await apolloClient.query<GetMemberQuery>({
            query: GetMemberDocument,
            variables: {
                logInput: operationInput,
                member_id: deliveryRegisterInputStore.user_id,
            },
            fetchPolicy: "no-cache",
          });
          memberData.value = member.data.getMember;
          if (memberData.value) {
            deliveryRequestCreateInput.delivery_address1 =
              memberData.value.address1;
            deliveryRequestCreateInput.delivery_address2 =
              memberData.value.address2;
            deliveryRequestCreateInput.delivery_address3 =
              memberData.value.address3;
            deliveryRequestCreateInput.delivery_name1 =
              memberData.value.name1;
            deliveryRequestCreateInput.delivery_name2 =
              memberData.value.name2;
            deliveryRequestCreateInput.delivery_name_kana1 =
              memberData.value.name_kana1;
            deliveryRequestCreateInput.delivery_name_kana2 =
              memberData.value.name_kana2;
            deliveryRequestCreateInput.delivery_phone =
              memberData.value.phone;
            deliveryRequestCreateInput.delivery_zip_code =
              memberData.value.zip_code;
            deliveryRequestCreateInput.member_id = Number(
              deliveryRegisterInputStore.user_id
            );
            deliveryRequestCreateInput.member_notes = memberData.value.notes;
            deliveryRequestCreateInput.house_card =
              memberData.value.house_card;
            house_card_before.value = deliveryRequestCreateInput.house_card!;
            if (house_card_before.value !== "") {
              house_card_change.value = true;
            } else {
              house_card_change.value = false;
            }
          }
        } catch (err: unknown) {
          if (err instanceof Error) {
            err.message = "会員情報の取得に失敗しました。";
            throw err;
          } else {
            throw new Error("会員情報の取得に失敗しました。");
          }
        }

        originalAddress.value = deliveryRequestCreateInput.delivery_address1;
        await getAreaList();
      }

      /**
       * 仮会員IDから情報取得する
       */
      if (deliveryRegisterInputStore.tmp_member_id !== "") {
        try {
          const tmpMember: ApolloQueryResult<GetTmpMemberQuery> =
          await apolloClient.query<GetTmpMemberQuery>({
            query: GetTmpMemberDocument,
            variables: {
                logInput: operationInput,
                tmp_member_id: deliveryRegisterInputStore.tmp_member_id,
            },
            fetchPolicy: "no-cache",
          });
          tmpMemberData.value = tmpMember.data.getTmpMember;
          if (tmpMemberData.value) {
            deliveryRequestCreateInput.delivery_address1 =
              tmpMemberData.value.address1;
            deliveryRequestCreateInput.delivery_address2 =
              tmpMemberData.value.address2;
            deliveryRequestCreateInput.delivery_address3 =
              tmpMemberData.value.address3;
            deliveryRequestCreateInput.delivery_name1 =
              tmpMemberData.value.name1;
            deliveryRequestCreateInput.delivery_name2 =
              tmpMemberData.value.name2;
            deliveryRequestCreateInput.delivery_name_kana1 =
              tmpMemberData.value.name_kana1;
            deliveryRequestCreateInput.delivery_name_kana2 =
              tmpMemberData.value.name_kana2;
            deliveryRequestCreateInput.delivery_phone =
              tmpMemberData.value.phone;
            deliveryRequestCreateInput.delivery_zip_code =
              tmpMemberData.value.zip_code;
          }
        } catch (err: any) {
          err.message = "仮会員情報の取得に失敗しました。";
          throw err;
        }
        originalAddress.value = deliveryRequestCreateInput.delivery_address1;
        await getAreaList();
      }

      // レシートバーコード設定時
      if (deliveryRegisterInputStore.house_card !== "") {
        deliveryRequestCreateInput.house_card =
          deliveryRegisterInputStore.house_card;
      }
      if (deliveryRegisterInputStore.register_no !== 0) {
        deliveryRequestCreateInput.register_no =
          deliveryRegisterInputStore.register_no;
      }
      if (deliveryRegisterInputStore.receipt_no !== 0) {
        deliveryRequestCreateInput.receipt_no =
          deliveryRegisterInputStore.receipt_no;
      }
      if (deliveryRegisterInputStore.payment_amount !== 0) {
        await commmaMoney(String(deliveryRegisterInputStore.payment_amount));
      }
    } catch (err: unknown) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    }
  };

  onMounted(() => {
    void runInitialLoad();
  });

  return { runInitialLoad };
};
